import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ShoppingCart, CheckCircle, X, AlertTriangle, Package,
  Calendar, MapPin, User, FileText, Download,
  Upload, Plus, Search, Filter, RefreshCw, Eye,
  Edit, Trash2, MoreHorizontal, ChevronRight,
  Clock, DollarSign, Scale, Box, Camera, QrCode,
  Barcode, Scan, Printer, Bell, UserCheck, Store,
  Home, Database, PieChart, LineChart, BarChart2,
  Truck, Building2, Users, Target, Route,
  Timer, CheckSquare, Square, Percent, TrendingUp,
  TrendingDown, ShoppingBag, CreditCard, Receipt
} from 'lucide-react';
import {
  LineChart as RechartsLineChart, Line, AreaChart, Area, BarChart as RechartsBarChart, Bar,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
  ResponsiveContainer, PieChart as RechartsPieChart,
  Pie, Cell, ComposedChart
} from 'recharts';

const OrderingManagement = () => {
  const [activeTab, setActiveTab] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedSupplier, setSelectedSupplier] = useState('all');
  const [selectedStatus, setSelectedStatus] = useState('all');
  const [showNewOrderModal, setShowNewOrderModal] = useState(false);
  const [newOrder, setNewOrder] = useState({
    supplier: '',
    items: [],
    expectedDelivery: '',
    notes: ''
  });

  // Mock data for orders
  const orderData = [
    {
      id: 'ORD-001',
      orderNumber: 'PO-2024-001',
      supplier: 'Coffee Co.',
      status: 'pending',
      priority: 'high',
      orderDate: '2024-01-15',
      expectedDelivery: '2024-01-22',
      totalValue: 2500.00,
      items: [
        { sku: 'COF-001', name: 'Premium Coffee Beans', quantity: 50, unitPrice: 25.00, total: 1250.00 },
        { sku: 'COF-002', name: 'Espresso Blend', quantity: 30, unitPrice: 28.00, total: 840.00 },
        { sku: 'COF-003', name: 'Decaf Coffee', quantity: 20, unitPrice: 20.50, total: 410.00 }
      ],
      notes: 'Urgent order for weekend rush',
      assignedTo: 'John Smith',
      location: 'Main Store',
      barcode: '1234567890123',
      documents: ['invoice.pdf', 'purchase-order.pdf'],
      paymentStatus: 'pending',
      paymentMethod: 'credit-card'
    },
    {
      id: 'ORD-002',
      orderNumber: 'PO-2024-002',
      supplier: 'Dairy Farm',
      status: 'approved',
      priority: 'medium',
      orderDate: '2024-01-14',
      expectedDelivery: '2024-01-20',
      totalValue: 1800.00,
      items: [
        { sku: 'MIL-001', name: 'Organic Milk', quantity: 100, unitPrice: 3.20, total: 320.00 },
        { sku: 'CHE-001', name: 'Cheddar Cheese', quantity: 50, unitPrice: 8.50, total: 425.00 },
        { sku: 'YOG-001', name: 'Greek Yogurt', quantity: 75, unitPrice: 4.20, total: 315.00 },
        { sku: 'BUT-001', name: 'Butter', quantity: 60, unitPrice: 4.50, total: 270.00 },
        { sku: 'CRE-001', name: 'Heavy Cream', quantity: 40, unitPrice: 6.75, total: 270.00 }
      ],
      notes: 'Regular weekly order',
      assignedTo: 'Sarah Johnson',
      location: 'All Stores',
      barcode: '1234567890124',
      documents: ['purchase-order.pdf'],
      paymentStatus: 'approved',
      paymentMethod: 'bank-transfer'
    },
    {
      id: 'ORD-003',
      orderNumber: 'PO-2024-003',
      supplier: 'Local Bakery',
      status: 'in-transit',
      priority: 'low',
      orderDate: '2024-01-13',
      expectedDelivery: '2024-01-18',
      totalValue: 950.00,
      items: [
        { sku: 'BREAD-001', name: 'Artisan Bread', quantity: 100, unitPrice: 2.50, total: 250.00 },
        { sku: 'PAST-001', name: 'Croissants', quantity: 80, unitPrice: 1.75, total: 140.00 },
        { sku: 'CAKE-001', name: 'Chocolate Cake', quantity: 20, unitPrice: 28.00, total: 560.00 }
      ],
      notes: 'Special order for birthday celebrations',
      assignedTo: 'Mike Wilson',
      location: 'Downtown Store',
      barcode: '1234567890125',
      documents: ['invoice.pdf', 'delivery-note.pdf'],
      paymentStatus: 'paid',
      paymentMethod: 'cash'
    },
    {
      id: 'ORD-004',
      orderNumber: 'PO-2024-004',
      supplier: 'Produce Market',
      status: 'delivered',
      priority: 'medium',
      orderDate: '2024-01-12',
      expectedDelivery: '2024-01-16',
      totalValue: 1200.00,
      items: [
        { sku: 'VEG-001', name: 'Fresh Tomatoes', quantity: 50, unitPrice: 2.00, total: 100.00 },
        { sku: 'VEG-002', name: 'Lettuce', quantity: 40, unitPrice: 1.50, total: 60.00 },
        { sku: 'FRUIT-001', name: 'Apples', quantity: 60, unitPrice: 1.75, total: 105.00 },
        { sku: 'FRUIT-002', name: 'Bananas', quantity: 80, unitPrice: 0.75, total: 60.00 },
        { sku: 'VEG-003', name: 'Carrots', quantity: 70, unitPrice: 1.25, total: 87.50 },
        { sku: 'FRUIT-003', name: 'Oranges', quantity: 65, unitPrice: 1.20, total: 78.00 },
        { sku: 'VEG-004', name: 'Onions', quantity: 45, unitPrice: 1.00, total: 45.00 },
        { sku: 'FRUIT-004', name: 'Grapes', quantity: 30, unitPrice: 3.50, total: 105.00 },
        { sku: 'VEG-005', name: 'Potatoes', quantity: 100, unitPrice: 0.80, total: 80.00 },
        { sku: 'FRUIT-005', name: 'Strawberries', quantity: 25, unitPrice: 4.00, total: 100.00 }
      ],
      notes: 'Fresh produce for the week',
      assignedTo: 'Lisa Brown',
      location: 'All Stores',
      barcode: '1234567890126',
      documents: ['invoice.pdf', 'receipt.pdf'],
      paymentStatus: 'paid',
      paymentMethod: 'credit-card'
    },
    {
      id: 'ORD-005',
      orderNumber: 'PO-2024-005',
      supplier: 'Electronics Supply',
      status: 'cancelled',
      priority: 'high',
      orderDate: '2024-01-11',
      expectedDelivery: '2024-01-25',
      totalValue: 5000.00,
      items: [
        { sku: 'TAB-001', name: 'Tablet POS', quantity: 5, unitPrice: 800.00, total: 4000.00 },
        { sku: 'PRN-001', name: 'Receipt Printer', quantity: 5, unitPrice: 200.00, total: 1000.00 }
      ],
      notes: 'Cancelled due to budget constraints',
      assignedTo: 'David Lee',
      location: 'Main Store',
      barcode: '1234567890127',
      documents: ['cancellation-notice.pdf'],
      paymentStatus: 'refunded',
      paymentMethod: 'credit-card'
    }
  ];

  // Get status color
  const getStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500 text-white';
      case 'approved': return 'bg-blue-500 text-white';
      case 'in-transit': return 'bg-purple-500 text-white';
      case 'delivered': return 'bg-green-500 text-white';
      case 'cancelled': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Get priority color
  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high': return 'bg-red-500 text-white';
      case 'medium': return 'bg-yellow-500 text-white';
      case 'low': return 'bg-green-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Get payment status color
  const getPaymentStatusColor = (status) => {
    switch (status) {
      case 'pending': return 'bg-yellow-500 text-white';
      case 'approved': return 'bg-blue-500 text-white';
      case 'paid': return 'bg-green-500 text-white';
      case 'refunded': return 'bg-red-500 text-white';
      default: return 'bg-gray-500 text-white';
    }
  };

  // Define tabs with counts
  const tabs = [
    { id: 'all', label: 'All', count: orderData.length },
    { id: 'pending', label: 'Pending', count: orderData.filter(order => order.status === 'pending').length },
    { id: 'approved', label: 'Approved', count: orderData.filter(order => order.status === 'approved').length },
    { id: 'in-transit', label: 'In Transit', count: orderData.filter(order => order.status === 'in-transit').length },
    { id: 'delivered', label: 'Delivered', count: orderData.filter(order => order.status === 'delivered').length },
    { id: 'cancelled', label: 'Cancelled', count: orderData.filter(order => order.status === 'cancelled').length }
  ];

  // Get unique suppliers
  const suppliers = [...new Set(orderData.map(order => order.supplier))];

  // Filter orders based on active tab and search
  const filteredOrders = orderData.filter(order => {
    const matchesTab = activeTab === 'all' || order.status === activeTab;
    const matchesSearch = order.orderNumber.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.supplier.toLowerCase().includes(searchQuery.toLowerCase()) ||
                         order.items.some(item => item.name.toLowerCase().includes(searchQuery.toLowerCase()));
    const matchesSupplier = selectedSupplier === 'all' || order.supplier === selectedSupplier;
    const matchesStatus = selectedStatus === 'all' || order.status === selectedStatus;
    
    return matchesTab && matchesSearch && matchesSupplier && matchesStatus;
  });

  // Order card component
  const NewOrderModal = () => (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-2xl font-bold text-gray-900">Create New Order</h2>
          <button
            onClick={() => setShowNewOrderModal(false)}
            className="p-2 rounded-lg hover:bg-gray-100 text-gray-600 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="space-y-6">
          {/* Supplier Selection */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Supplier</label>
            <select
              value={newOrder.supplier}
              onChange={(e) => setNewOrder({...newOrder, supplier: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="">Select a supplier</option>
              {suppliers.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>
          </div>

          {/* Expected Delivery */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Expected Delivery Date</label>
            <input
              type="date"
              value={newOrder.expectedDelivery}
              onChange={(e) => setNewOrder({...newOrder, expectedDelivery: e.target.value})}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Notes</label>
            <textarea
              value={newOrder.notes}
              onChange={(e) => setNewOrder({...newOrder, notes: e.target.value})}
              rows={3}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              placeholder="Add any special instructions or notes..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex items-center justify-end space-x-3 pt-4 border-t border-gray-200">
            <button
              onClick={() => setShowNewOrderModal(false)}
              className="px-6 py-3 text-gray-700 bg-gray-100 rounded-xl hover:bg-gray-200 transition-colors font-medium"
            >
              Cancel
            </button>
            <button
              onClick={() => {
                if (newOrder.supplier && newOrder.expectedDelivery) {
                  alert(`New order created for ${newOrder.supplier} with delivery date ${newOrder.expectedDelivery}`);
                  setShowNewOrderModal(false);
                  setNewOrder({ supplier: '', items: [], expectedDelivery: '', notes: '' });
                } else {
                  alert('Please fill in all required fields');
                }
              }}
              className="px-6 py-3 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-xl hover:from-blue-700 hover:to-blue-800 transition-all duration-200 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              Create Order
            </button>
          </div>
        </div>
      </div>
    </div>
  );

  const OrderCard = ({ order }) => (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
    >
      {/* Header */}
      <div className="p-6 border-b border-gray-100">
        <div className="flex items-start justify-between mb-4">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">{order.orderNumber}</h3>
            <p className="text-sm text-gray-600">{order.supplier}</p>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getStatusColor(order.status)}`}>
              {order.status.charAt(0).toUpperCase() + order.status.slice(1)}
            </span>
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityColor(order.priority)}`}>
              {order.priority.charAt(0).toUpperCase() + order.priority.slice(1)}
            </span>
          </div>
        </div>

        {/* Order Details */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-500">Order Date:</span>
            <p className="font-medium text-gray-900">{order.orderDate}</p>
          </div>
          <div>
            <span className="text-gray-500">Expected Delivery:</span>
            <p className="font-medium text-gray-900">{order.expectedDelivery}</p>
          </div>
          <div>
            <span className="text-gray-500">Total Value:</span>
            <p className="font-medium text-gray-900">${order.totalValue.toLocaleString()}</p>
          </div>
          <div>
            <span className="text-gray-500">Assigned To:</span>
            <p className="font-medium text-gray-900">{order.assignedTo}</p>
          </div>
        </div>
      </div>

      {/* Items */}
      <div className="p-6">
        <h4 className="font-semibold text-gray-900 mb-3">Order Items ({order.items.length})</h4>
        <div className="space-y-2">
          {order.items.slice(0, 3).map((item, index) => (
            <div key={index} className="flex justify-between items-center py-2 border-b border-gray-50 last:border-b-0">
              <div>
                <p className="font-medium text-gray-900">{item.name}</p>
                <p className="text-sm text-gray-600">SKU: {item.sku}</p>
              </div>
              <div className="text-right">
                <p className="font-medium text-gray-900">{item.quantity} × ${item.unitPrice}</p>
                <p className="text-sm text-gray-600">${item.total.toLocaleString()}</p>
              </div>
            </div>
          ))}
          {order.items.length > 3 && (
            <p className="text-sm text-gray-500 text-center py-2">
              +{order.items.length - 3} more items
            </p>
          )}
        </div>

        {/* Payment Status */}
        <div className="mt-4 p-3 bg-gray-50 rounded-lg">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Payment Status</p>
              <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPaymentStatusColor(order.paymentStatus)}`}>
                {order.paymentStatus.charAt(0).toUpperCase() + order.paymentStatus.slice(1)}
              </span>
            </div>
            <div className="text-right">
              <p className="text-sm text-gray-600">Payment Method</p>
              <p className="text-sm font-medium text-gray-900">{order.paymentMethod.replace('-', ' ')}</p>
            </div>
          </div>
        </div>

        {/* Notes */}
        {order.notes && (
          <div className="mt-4 p-3 bg-blue-50 rounded-lg">
            <p className="text-sm text-blue-800">{order.notes}</p>
          </div>
        )}

        {/* Actions */}
        <div className="mt-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <Eye className="w-4 h-4" />
            </button>
            <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <Edit className="w-4 h-4" />
            </button>
            <button className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors">
              <Printer className="w-4 h-4" />
            </button>
          </div>
          <div className="flex items-center gap-2">
            {order.status === 'pending' && (
              <button className="px-3 py-1 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium">
                Approve
              </button>
            )}
            {order.status === 'approved' && (
              <button className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium">
                Mark In Transit
              </button>
            )}
            {order.status === 'in-transit' && (
              <button className="px-3 py-1 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors text-sm font-medium">
                Mark Delivered
              </button>
            )}
            {order.status === 'pending' && (
              <button className="px-3 py-1 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors text-sm font-medium">
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">Order Management</h2>
            <p className="text-gray-600">Create and manage purchase orders for inventory</p>
          </div>
          <div className="flex items-center space-x-3">
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={() => setShowNewOrderModal(true)}
              className="px-4 py-2 bg-gradient-to-r from-blue-600 to-blue-700 text-white rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 flex items-center gap-2 font-medium shadow-lg hover:shadow-xl transform hover:scale-105"
            >
              <Plus className="w-4 h-4" />
              New Order
            </motion.button>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors flex items-center gap-2"
            >
              <Download className="w-4 h-4" />
              Export Orders
            </motion.button>
          </div>
        </div>

        {/* Apple-like Tabs */}
        <div className="flex justify-center mb-6">
          <div className="inline-flex bg-gray-100 rounded-xl p-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 flex items-center gap-2 ${
                  activeTab === tab.id
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900 hover:bg-white/50'
                }`}
              >
                {tab.label}
                <span className={`px-2 py-0.5 rounded-full text-xs ${
                  activeTab === tab.id
                    ? 'bg-blue-100 text-blue-700'
                    : 'bg-gray-200 text-gray-600'
                }`}>
                  {tab.count}
                </span>
              </button>
            ))}
          </div>
        </div>

        {/* Search and Filters */}
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search by order number, supplier, or item..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <select
              value={selectedSupplier}
              onChange={(e) => setSelectedSupplier(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="all">All Suppliers</option>
              {suppliers.map(supplier => (
                <option key={supplier} value={supplier}>{supplier}</option>
              ))}
            </select>
            <select
              value={selectedStatus}
              onChange={(e) => setSelectedStatus(e.target.value)}
              className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="pending">Pending</option>
              <option value="approved">Approved</option>
              <option value="in-transit">In Transit</option>
              <option value="delivered">Delivered</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <button className="px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2">
              <Filter className="w-4 h-4" />
              Advanced
            </button>
          </div>
        </div>
      </div>

      {/* Orders Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <AnimatePresence>
          {filteredOrders.map((order) => (
            <OrderCard key={order.id} order={order} />
          ))}
        </AnimatePresence>
      </div>

      {/* Empty State */}
      {filteredOrders.length === 0 && (
        <div className="text-center py-12">
          <ShoppingCart className="w-16 h-16 mx-auto mb-4 text-gray-400" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No orders found</h3>
          <p className="text-gray-600 mb-4">Try adjusting your search criteria or create a new order</p>
          <button
            onClick={() => setShowNewOrderModal(true)}
            className="px-4 py-2 bg-blue-700 text-white rounded-lg hover:bg-blue-800 transition-colors"
          >
            Create New Order
          </button>
        </div>
      )}

      {/* New Order Modal */}
      {showNewOrderModal && <NewOrderModal />}
    </div>
  );
};

export default OrderingManagement; 